/**
 * Urgency is computed, never stored. Derived from due_date and stage so the board,
 * the API and the Slack digest can never disagree about what is overdue.
 */
export const URGENCIES = ["overdue", "due_soon", "normal"];
/** Days before the due date at which a sticky starts pulsing amber. */
export const DUE_SOON_DAYS = 3;
/** Local calendar day as YYYY-MM-DD. Due dates are dates, not instants. */
export function today(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
function daysBetween(fromIso, toIso) {
    const from = Date.parse(`${fromIso}T00:00:00Z`);
    const to = Date.parse(`${toIso}T00:00:00Z`);
    return Math.round((to - from) / 86_400_000);
}
export function urgencyOf(task, now = new Date(), dueSoonDays = DUE_SOON_DAYS) {
    // Completed tasks never pulse, regardless of due date.
    if (task.stage === "completed" || !task.due_date)
        return "normal";
    const delta = daysBetween(today(now), task.due_date);
    if (delta < 0)
        return "overdue";
    if (delta <= dueSoonDays)
        return "due_soon";
    return "normal";
}
const URGENCY_RANK = { overdue: 0, due_soon: 1, normal: 2 };
/** How much a task matters, independent of when it is due. */
export const PRIORITIES = ["urgent", "high", "normal", "low"];
/** Priorities that get a pulsing outline on the board. */
export function isLoud(priority) {
    return priority === "urgent" || priority === "high";
}
const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
/**
 * Priority first, then urgency, then soonest due date, then oldest created.
 * Used by the bands and by tidy.
 *
 * Priority leading is a deliberate choice and a real trade: an urgent task that
 * is not due yet sorts above one that is already overdue. That is what "move
 * the higher priority stickies to the top of their section" asks for — how much
 * something matters is treated as a stronger signal than when it was due. Swap
 * the two blocks below to reverse it.
 */
export function compareByUrgency(a, b, now = new Date()) {
    const priority = PRIORITY_RANK[a.priority ?? "normal"] - PRIORITY_RANK[b.priority ?? "normal"];
    if (priority !== 0)
        return priority;
    const rank = URGENCY_RANK[urgencyOf(a, now)] - URGENCY_RANK[urgencyOf(b, now)];
    if (rank !== 0)
        return rank;
    if (a.due_date !== b.due_date) {
        if (!a.due_date)
            return 1;
        if (!b.due_date)
            return -1;
        return a.due_date < b.due_date ? -1 : 1;
    }
    return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0;
}
//# sourceMappingURL=urgency.js.map